import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { noop } from 'lodash';
import styled from 'styled-components';
import { buildPublicUrl } from '../../utils/buildUrl';
import { getAdId } from '../../utils/getAdId';
import { useIsMobile } from '../../utils/getIsMobile';
import { getTheme } from '../../utils/getTheme';
import { useTranslation } from '../../utils/useTranslation';
import { APP_DOWNLOAD_LINK } from '../discovery/AppDiscoveryMobile';
import { Skeleton } from '../feedback/Skeleton';
import { IconLogo } from '../icon/IconLogo';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Image } from '../media/Image';
import { Typography } from '../typography/Typography';

const COMPACT_CARD_FOOTER_HEIGHT = 84;

const PostAdCardCompactSkeleton: FC = () => (
    <Box width="100%" height="100%" position="absolute" top={0} left={0}>
        <Skeleton
            width="100%"
            height={`calc(100% - ${COMPACT_CARD_FOOTER_HEIGHT}px)`}
            variant="rect"
            borderRadius={2}
        />
        <Flex width="100%" height={COMPACT_CARD_FOOTER_HEIGHT} px={0.5} pt={1} pb={2} spacing={0.5}>
            <Flex width="100%" direction="row" alignItems="center">
                <Skeleton width={24} height={24} variant="circular" />
                <Skeleton width="30%" height={14} variant="rect" ml={1} />
            </Flex>
            <Skeleton width="100%" height={28} variant="rect" />
        </Flex>
    </Box>
);

const ALT_IMAGES_COUNT = 5;

interface PostAdCardCompactAltContainerProps {
    children: React.ReactNode;
    onPostAdCardCompactAltClick: () => void;
}

const PostAdCardCompactAltContainer: FC<PostAdCardCompactAltContainerProps> = ({
    children,
    onPostAdCardCompactAltClick,
}) => {
    const isMobile = useIsMobile();

    if (isMobile) {
        return (
            <Box
                as="a"
                href={APP_DOWNLOAD_LINK}
                target="_blank"
                width="100%"
                height="100%"
                position="absolute"
                top={0}
                left={0}
                textDecoration="none"
            >
                {children}
            </Box>
        );
    } else {
        return (
            <Box
                width="100%"
                height="100%"
                position="absolute"
                top={0}
                left={0}
                cursor="pointer"
                onClick={onPostAdCardCompactAltClick}
            >
                {children}
            </Box>
        );
    }
};

interface PostAdCardCompactAltProps {
    onPostAdCardCompactAltClick: () => void;
}

const PostAdCardCompactText = styled(Typography)
    .withConfig({ displayName: 'PostAdCardCompactText' })
    .attrs((props) => ({
        ...props,
        variant: 'caption',
        cursor: 'pointer',
    }))`
    height: 28px;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    word-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
`;

const PostAdCardCompactAlt: FC<PostAdCardCompactAltProps> = ({ onPostAdCardCompactAltClick }) => {
    const t = useTranslation();
    const currentAltImageIndex = useMemo(
        () => Math.floor(Math.random() * ALT_IMAGES_COUNT) + 1,
        [],
    );

    return (
        <PostAdCardCompactAltContainer onPostAdCardCompactAltClick={onPostAdCardCompactAltClick}>
            <Box borderRadius={2} overflow="hidden">
                <Image src={buildPublicUrl(`/images/altAd${currentAltImageIndex}.jpg`)} />
            </Box>
            <Flex
                width="100%"
                height={COMPACT_CARD_FOOTER_HEIGHT}
                px={0.5}
                pt={1}
                pb={2}
                spacing={0.5}
            >
                <Flex width="100%" direction="row" alignItems="center">
                    <IconLogo fontSize={24} style={{ borderRadius: '100%' }} />
                    <Typography variant="caption" weight="bold" ml={1}>
                        {t('Яндекс Ритм')}
                    </Typography>
                </Flex>
                <PostAdCardCompactText>
                    {t('Скачивайте приложение Яндекс Ритм, чтобы было удобнее пользоваться.')}
                </PostAdCardCompactText>
            </Flex>
        </PostAdCardCompactAltContainer>
    );
};

export const AD_COMPACT_FOOTER_HEIGHT = 32;

const adIds = ['R-I-10074220-6', 'R-I-10074220-7', 'R-I-10074220-8'];

interface PostAdCardCompactProps {
    onPostAdCardCompactClick?: () => void;
    postIndex: number;
    adInterval: number;
}

export const PostAdCardCompact: FC<PostAdCardCompactProps> = ({
    onPostAdCardCompactClick = noop,
    postIndex,
    adInterval,
}) => {
    const [isSuccess, setIsSuccess] = useState(false);
    const [altEnabled, setAltEnabled] = useState(false);
    const isMobile = useIsMobile();

    const id = getAdId(adIds, postIndex, adInterval);
    const blockId = useRef((Math.random() * 10000000).toFixed(0));

    useEffect(() => {
        // @ts-ignore
        if (typeof isMobile === 'boolean' && window.yaContextCb) {
            // @ts-ignore
            window.yaContextCb.push(() => {
                // @ts-ignore
                Ya.Context.AdvManager.render({
                    blockId: id,
                    renderTo: `yandex_rtb_${id}_${blockId.current}`,
                    darkTheme: getTheme() === 'dark',
                    allowRepeatAds: { maxSkipSize: 50 },
                    altCallback: () => setAltEnabled(true),
                    onRender: () => setIsSuccess(true),
                });
            });
            // @ts-ignore
        } else if (!window.yaContextCb) {
            setAltEnabled(true);
        }
    }, [id, isMobile]);

    const skeletonEnabled = !isSuccess && !altEnabled;

    return (
        <Box width="100%" height="100%" position="relative">
            {skeletonEnabled ? <PostAdCardCompactSkeleton /> : null}
            {altEnabled ? (
                <PostAdCardCompactAlt onPostAdCardCompactAltClick={onPostAdCardCompactClick} />
            ) : null}
            <Box id={`yandex_rtb_${id}_${blockId.current}`} width="100%" height="100%"></Box>
        </Box>
    );
};
